import React, { useEffect, useMemo, useState } from 'react';

import activeEditor from 'domains/active_editor';

import crops from 'domains/crops';
import session from 'domains/session';
import stage from 'domains/stage';
import rooms from 'domains/wall_preview_rooms';

import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { renderModal } from 'utils/modal_util';

import Button from 'components/shared/Button';
import ShareWallPreviewModal from './ShareWallPreviewModal';

function WallPreviewTools() {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(session.selectors.isLoggedIn);
  const wallPreviewEnabled = useAppSelector(stage.selectors.wallPreviewActive);
  const activeCatalog = useAppSelector(activeEditor.selectors.catalog);
  const activeSubcatalog = useAppSelector(activeEditor.selectors.subcatalog);
  const activeOptions = useAppSelector(activeEditor.selectors.selectedOptions);
  const cropIds = useAppSelector(crops.selectors.selectIds);
  const potentialCropIds = useAppSelector(crops.selectors.potentialCropIds);

  const [showShareDialog, setShowShareDialog] = useState(false);

  const hasEmptyNodes = useMemo(
    () => cropIds.length !== potentialCropIds.length,
    [cropIds, potentialCropIds]
  );

  useEffect(() => {
    if (activeSubcatalog && !activeSubcatalog.wall_display) {
      dispatch(rooms.actions.unsetWhichWallPreview());
    }
  }, [dispatch, activeSubcatalog]);

  // This is extremely specific and we need to fix the underlying issue, but to get to release
  // we are targeting these option ids to hide the mask node since it is sized weird on horizontal
  // epic prints with bounds > 0
  // see https://jira.discdrive.bayphoto.com/projects/WWD/issues/WWD-14
  function epicPrintsHack(appStageEl: HTMLElement) {
    const epicPrintsHackClass = 'epic-prints-hack';

    if (activeCatalog?.name === 'Epic Prints') {
      appStageEl.classList.add('epic-prints');
      const options = activeOptions.map((option) => option.name);

      const noHangerOrInsetSelected = (option: string) => {
        return (
          option.toLowerCase().indexOf('no hanger') !== -1 ||
          option.toLowerCase().indexOf('inset') !== -1
        );
      };

      if (options.some(noHangerOrInsetSelected)) {
        appStageEl.classList.add(epicPrintsHackClass);
      } else {
        appStageEl.classList.remove(epicPrintsHackClass);
      }
    }
  }

  useEffect(() => {
    const appStageEl = document.getElementById('app_stage');

    if (appStageEl) {
      if (wallPreviewEnabled) {
        epicPrintsHack(appStageEl);
      } else {
        appStageEl.classList.remove('epic-prints');
        appStageEl.classList.remove('epic-prints-hack');
      }
    }
  });

  function handleExitPreview() {
    dispatch(rooms.actions.unsetWhichWallPreview());
  }

  function handleSharePreview() {
    if (!hasEmptyNodes) {
      setShowShareDialog(true);
    }
  }

  return (
    <React.Fragment>
      <Button
        type="icon"
        icon="wallPreviewShare"
        size="xl"
        a11yText="Save/Share Preview"
        onClick={handleSharePreview}
        disabled={!isLoggedIn || hasEmptyNodes || !wallPreviewEnabled}
        tooltipProps={{ placement: 'left' }}
      />
      <Button
        type="icon"
        icon="wallPreviewClose"
        size="xl"
        a11yText="Exit Wall Preview"
        onClick={handleExitPreview}
        disabled={!wallPreviewEnabled}
        tooltipProps={{ placement: 'left' }}
      />

      {showShareDialog &&
        renderModal(<ShareWallPreviewModal onClose={() => setShowShareDialog(false)} />)}
    </React.Fragment>
  );
}

export default WallPreviewTools;
